import { z } from 'zod'

export const cardPortalCredentialsSchema = z.object({
  clientId: z.string().min(1, 'Client ID is required'),
  clientSecret: z.string().min(1, 'Client secret is required'),
})

export const integrationSettingsSchema = z.object({
  autoSync: z.boolean().default(false),
  syncInterval: z.enum(['hourly', 'daily', 'weekly']).default('daily'),
  syncStartDate: z.string().optional(),
  notifyOnError: z.boolean().default(true),
})

export type CardPortalCredentialsInput = z.infer<typeof cardPortalCredentialsSchema>
export type IntegrationSettingsInput = z.infer<typeof integrationSettingsSchema>

/**
 * Represents a single AP check header record (APPYMT) returned by the Sage
 * Intacct XML Gateway. All fields except RECORDNO are optional because Sage
 * may omit empty fields entirely in the XML response. Field names are kept
 * uppercase to match the Sage object schema verbatim — callers map them to
 * snake_case DB columns explicitly.
 *
 * TOTALPAID uses coerce because Sage sometimes serialises numeric fields as
 * strings in older XML payloads. RECORDNO likewise coerces from string.
 * STATE and CLEARED are not enum-restricted here — we log unexpected values
 * rather than rejecting the whole batch on a schema change from Sage.
 */
/**
 * `z.coerce.number()` happily produces `NaN` for non-numeric strings — Sage
 * has occasionally been observed emitting `"N/A"` or other text in numeric
 * fields when a downstream report fails to populate them. It will also produce
 * `Infinity` / `-Infinity` for inputs like the literal string `"Infinity"`,
 * which would survive a NaN-only refine and corrupt every downstream sum
 * (book balance, outstanding totals) by propagating ±∞ through the arithmetic.
 *
 * `Number.isFinite` rejects all three (`NaN`, `Infinity`, `-Infinity`) in a
 * single check, matching the user-facing "Must be a finite number" message.
 * The operator sees that clearly in `sage_sync_state.last_error` instead of a
 * Postgres NUMERIC insert error or silent 0/NULL coercion.
 */
const REJECT_NAN = { message: 'Must be a finite number' } as const

export const sageCheckSchema = z.object({
  /** Sage canonical integer primary key from APPYMT.RECORDNO */
  RECORDNO: z.coerce
    .number()
    .int()
    .refine((val) => Number.isFinite(val), REJECT_NAN),
  /** Human-readable check identifier (e.g. "CHK-001") */
  RECORDID: z.string().optional(),
  /** Check number printed on the paper check (APPYMT.DOCNUMBER) */
  DOCNUMBER: z.string().optional(),
  /** Payment method string as returned by Sage (e.g. "Printed Check") */
  PAYMENTMETHOD: z.string().optional(),
  /** One of C/D/A/V/S — not enum-restricted so schema drift doesn't break syncs */
  STATE: z.string().optional(),
  /** Sage vendor ID for the AP payment */
  VENDORID: z.string().optional(),
  /** Sage vendor display name */
  VENDORNAME: z.string().optional(),
  /** Sage bank account entity ID (maps to FINANCIALENTITY in Sage) */
  FINANCIALENTITY: z.string().optional(),
  /** Human-readable name for the financial account */
  FINANCIALACCOUNTNAME: z.string().optional(),
  /** Total amount the check was issued for (APPYMT.TOTALENTERED). Always
   *  populated — for STATE='A' (Approved) rows this is the only field that
   *  carries the actual check amount because TOTALPAID stays 0 until Post. */
  TOTALENTERED: z.coerce
    .number()
    .refine((val) => Number.isFinite(val), REJECT_NAN)
    .default(0),
  /** Header total paid (APPYMT.TOTALPAID) — populated only after Post; 0 for
   *  Approved checks. Stored verbatim alongside TOTALENTERED so callers can
   *  distinguish "issued" vs "posted" amounts without inferring from STATE. */
  TOTALPAID: z.coerce
    .number()
    .refine((val) => Number.isFinite(val), REJECT_NAN)
    .default(0),
  /** Outstanding portion of the check (APPYMT.TOTALDUE). Non-zero while the
   *  check is approved-but-not-posted; drops to 0 after Post. */
  TOTALDUE: z.coerce
    .number()
    .refine((val) => Number.isFinite(val), REJECT_NAN)
    .default(0),
  /** ISO 4217 currency code (e.g. "USD") */
  CURRENCY: z.string().optional(),
  /** Scheduled payment date (APPYMT.WHENDUE). Populated for STATE='A' rows
   *  where Sage hasn't posted the check yet and WHENPAID is still empty. */
  WHENDUE: z.string().optional(),
  /** Actual payment date (APPYMT.WHENPAID); empty string for not-yet-posted
   *  checks. Stored as-is — no fallback to WHENDUE. */
  WHENPAID: z.string().optional(),
  /** GL post date (APPYMT.WHENPOSTED); empty until the check is Posted. */
  WHENPOSTED: z.string().optional(),
  /** Pre-localised display label for STATE (APPYMT.DISPLAYSTATE), e.g.
   *  "Approved", "Posted", "Voided" — used for UI display without translating
   *  the raw single-letter STATE codes. */
  DISPLAYSTATE: z.string().optional(),
  /** Sage TIMESTAMP of last modification; used as incremental sync cursor */
  WHENMODIFIED: z.string().optional(),
  /** Free-text check description */
  DESCRIPTION: z.string().optional(),
  /** Sage APPYMT reconciliation flag. TRI-STATE: 'T' cleared, 'M' matched
   *  (status prior to reconciliation), 'F' unreconciled. Not enum-restricted
   *  on purpose, same reasoning as STATE above — a schema drift here must not
   *  break syncs. In transit is `CLEARED <> 'T'`, never `= 'F'` — 'M' rows
   *  really exist in the customer's data. */
  CLEARED: z.string().optional(),
  /** Bank-statement reconciliation date, populated iff CLEARED === 'T'. NOT
   *  the date the check was cashed (it is almost always a month-end). Must
   *  never be written into sage_checks.date_cashed. */
  CLRDATE: z.string().optional(),
})

export type SageCheck = z.infer<typeof sageCheckSchema>

/**
 * Minimal VENDOR projection used by the check sync to resolve the Outstanding
 * Checks "Payee" (DCP-213 AC#1).
 *
 * APPYMT does not carry the name printed on the check; that value lives on the
 * related VENDOR object's contact as DISPLAYCONTACT.PRINTAS ("Print To" /
 * "Print As"). The Sage XML Gateway flattens related-object fields under their
 * literal dotted key, so the property name `'DISPLAYCONTACT.PRINTAS'` matches
 * Sage's response verbatim — it is NOT a nested object. PRINTAS is optional
 * because a vendor may have no Print-As configured, in which case the sync
 * falls back to VENDORNAME.
 */
export const sageVendorSchema = z.object({
  /** Sage vendor natural key (joins to APPYMT.VENDORID). */
  VENDORID: z.string(),
  /** Flattened VENDOR DISPLAYCONTACT.PRINTAS — the name printed on checks. */
  'DISPLAYCONTACT.PRINTAS': z.string().optional(),
})

export type SageVendor = z.infer<typeof sageVendorSchema>

/**
 * Structured warning emitted by a Sage sync run. Surfaces conditions that are
 * not hard errors but should be visible in the UI (e.g. missing prerequisites,
 * unmatched locations). Persisted as JSONB on sage_sync_state.warnings.
 *
 * `kind` is an open string so additional warning types can be added per task
 * without forcing a shared schema migration. Extra context (e.g. a Sage
 * location id, a facility name) goes into the catch-all index signature.
 */
export const sageSyncWarningSchema = z.object({
  /** Discriminator string identifying the warning kind (e.g. 'no_bank_accounts'). */
  kind: z.string(),
  /** Operator-facing description displayed verbatim in the UI. */
  message: z.string().optional(),
}).catchall(z.unknown())

export type SageSyncWarning = z.infer<typeof sageSyncWarningSchema>

/**
 * Strict variant of {@link sageSyncWarningSchema} used by the UI layer to
 * narrow each warning to a renderable shape. Items in `sage_sync_state.warnings`
 * that don't match one of these kinds are dropped by {@link parseSageSyncWarnings}
 * so unknown future kinds never crash the page.
 *
 * Kinds today, all emitted by the sync tasks:
 * - `unmatched_location`   — facility has a sage_location_id but Sage returned 0 bank accounts
 * - `no_facility_locations` — org has no facilities with a sage_location_id set
 * - `no_bank_accounts`     — Sage bank-accounts catalog is empty (run Sync Bank Accounts first)
 * - `result_truncated`     — readAllByQuery hit the page cap; the run finished
 *                             but may be missing rows. Operator can lift the cap
 *                             via SAGE_READ_MORE_PAGE_CAP if legitimate.
 * - `partial_failure`      — one or more per-row upserts failed mid-run; the
 *                             aggregate counts and last_error still describe
 *                             the failure, but this warning makes the
 *                             partial-success state explicit in the UI so a
 *                             completed-with-errors run isn't visually
 *                             indistinguishable from a clean success.
 * - `existing_snapshot_truncated` — the check sync's existing-record_no
 *                             snapshot hit its page cap, so that run's
 *                             created/updated split under-reports updates.
 *                             Synced data is unaffected.
 * - `invalid_location_id`  — one or more Sage LOCATIONIDs don't match the
 *                             `facilities.sage_location_id` DB CHECK format
 *                             (letters/digits/hyphen/underscore, 1-100 chars)
 *                             and were skipped BEFORE insert, not counted as
 *                             partial_failure. A permanent condition until the
 *                             offending id is fixed in Sage.
 */
const unmatchedLocationWarningSchema = z.object({
  kind: z.literal('unmatched_location'),
  sage_location_id: z.string(),
  facility_name: z.string(),
  message: z.string().optional(),
})

const simpleSageSyncWarningSchema = z.object({
  kind: z.enum([
    'no_facility_locations',
    'no_bank_accounts',
    // DCP-262: distinguishes "no ACTIVE facilities" from "no bank accounts
    // catalogued yet" — the two have different remedies (activate a facility
    // vs. run Sync Bank Accounts), so they must not share the misleading
    // "run Sync Bank Accounts first" message.
    'no_active_facilities',
    'result_truncated',
    'partial_failure',
    // DCP-234 review #30333 #7: state read for sage_sync_state failed →
    // task fell back to full fetch instead of delta. Surfaced so a
    // recurring DB/RLS issue doesn't silently keep paying for full
    // re-fetches that mask the underlying problem.
    'state_read_failed',
    // DCP-262 PR #156 review #2: a Sage LOCATIONID that doesn't match the
    // facilities.sage_location_id DB CHECK format was pre-filtered and
    // skipped before insert (see sync-facilities.ts SAGE_LOCATION_ID_PATTERN).
    'invalid_location_id',
    // DCP-320: the tenant read that feeds the reconciliation re-read either
    // failed or was cut short by its page cap. That re-read is the ONLY way a
    // prior-year check can ever leave Outstanding Checks, so a run that lost it
    // must not present as a clean pass.
    //
    // This enum is a CLOSED list and `parseSageSyncWarnings` DROPS anything it
    // does not recognise (to a `console.debug` nobody reads). A `warnings.push`
    // in a sync task is therefore only HALF a warning: omit the kind here and
    // the signal is written to `sage_sync_state.warnings` and then discarded
    // before it can reach a screen. Any new kind must land here in the same
    // commit as the push that raises it.
    'reconcile_read_failed',
    'reconcile_read_truncated',
    // DCP-320 review: the check sync's existing-record_no snapshot hit its own
    // page cap. Deliberately NOT folded into 'reconcile_read_truncated' — that
    // one means prior-year checks can no longer close, while this one means
    // only that a run's created/updated split under-reports updates. One
    // message cannot honestly carry both remedies.
    'existing_snapshot_truncated',
  ]),
  message: z.string(),
})

export const renderedSageSyncWarningSchema = z.union([
  unmatchedLocationWarningSchema,
  simpleSageSyncWarningSchema,
])

export type RenderedSageSyncWarning = z.infer<typeof renderedSageSyncWarningSchema>

/**
 * Coerce a raw `sage_sync_state.warnings` JSONB value (typed as unknown by the
 * Supabase generated types) into a typed array of {@link RenderedSageSyncWarning}.
 *
 * Items that fail validation are dropped so the UI can iterate the result
 * without runtime guards, but each drop is reported via `console.debug` so a
 * misconfigured warning (e.g. a new `kind` written by the sync task that
 * hasn't been added to {@link renderedSageSyncWarningSchema}) doesn't
 * vanish silently — operators can grep for "parseSageSyncWarnings: dropped"
 * in browser devtools to find the missing schema update.
 */
export function parseSageSyncWarnings(raw: unknown): RenderedSageSyncWarning[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item) => {
    const result = renderedSageSyncWarningSchema.safeParse(item)
    if (!result.success) {
      // debug level on purpose — these drops are expected for older rows
      // that pre-date a new schema variant. Worth seeing in devtools but
      // not worth spamming production logs with `warn`/`error`.
      console.debug('parseSageSyncWarnings: dropped invalid warning', {
        item,
        issues: result.error.issues,
      })
      return []
    }
    return [result.data]
  })
}

/**
 * Response shape returned after a Sage Intacct check sync run.
 * Intentionally omits `marked_missing_count` because checks only accumulate —
 * voids stay in the DB, so we never soft-delete or flag missing rows.
 */
export interface SageCheckSyncResult {
  /** Total number of Sage checks successfully processed in this run */
  synced_count: number
  /** Number of new rows inserted into sage_checks */
  created_count: number
  /** Number of existing rows updated in sage_checks */
  updated_count: number
  /** List of per-row error messages collected during the sync run */
  errors: string[]
  /** Structured warnings persisted to sage_sync_state.warnings. */
  warnings: SageSyncWarning[]
}

/**
 * Represents a single bank account record returned by the Sage Intacct XML
 * Gateway from either the CHECKINGACCOUNT or SAVINGSACCOUNT object. Both
 * objects share the same field shape so a single schema is sufficient — the
 * sync task tags rows with `account_type` before persisting.
 *
 * BANKACCOUNTID is the natural key (e.g. "DCP_TEST_BANK", "PH_BF934") and
 * lives in the same value space as APPYMT.FINANCIALENTITY, which is how AP
 * checks are joined to bank accounts. CHECKINGACCOUNT has no NAME field, so
 * we use GLACCOUNTTITLE for display (falling back to BANKACCOUNTID).
 */
export const sageBankAccountSchema = z.object({
  /** Sage natural key (e.g. "DCP_TEST_BANK"). Same value space as APPYMT.FINANCIALENTITY. */
  BANKACCOUNTID: z.string(),
  /** Real bank account number used for fuzzy matching against DCP bank_accounts */
  BANKACCOUNTNO: z.string().optional(),
  /** GL account title — e.g. "DCP_TEST Cash (operating bank)". Used as display name. */
  GLACCOUNTTITLE: z.string().optional(),
  /** Bank institution name, e.g. "Wells Fargo" */
  BANKNAME: z.string().optional(),
  /** Routing number; often blank in Sage payloads */
  ROUTINGNO: z.string().optional(),
  /** ISO 4217 currency code (e.g. "USD"); empty string when unset */
  CURRENCY: z.string().optional(),
  /**
   * Sage active/inactive lifecycle; absent rows default to 'active' downstream.
   *
   * `z.preprocess` normalises mixed-case payloads to lowercase BEFORE the
   * enum check, because Sage's documented contract is lowercase but
   * historical XML responses occasionally return `Active` / `INACTIVE`
   * (see the equivalent normalisation in sync-bank-accounts.ts that used
   * to be the only line guarding against this). Without the preprocess
   * step a single mixed-case row would throw a ZodError and fail the
   * whole sync run.
   *
   * The preprocess also handles non-string values defensively: numbers /
   * booleans / objects from an unexpected XML deserialization quirk are
   * coerced to a string first so the resulting ZodError points at the
   * STATUS field with an enum-mismatch message (`Invalid enum value …`)
   * instead of a confusing "Expected string, received number" from the
   * inner enum guard. `null` and `undefined` pass through unchanged so
   * the outer `.optional()` keeps absent rows valid.
   */
  STATUS: z
    .preprocess((val) => {
      if (val === null || val === undefined) return val
      return String(val).toLowerCase()
    }, z.enum(['active', 'inactive']))
    .optional(),
  /** Sage LOCATIONID — matches public.facilities.sage_location_id for this tenant. */
  LOCATIONID: z.string().optional(),
  /** GL account number associated with this bank account (e.g. "1000.00").
   *  Used to query GLACCOUNTBALANCE for the Intacct balance sync (DCP-159). */
  GLACCOUNTNO: z.string().optional(),
})

export type SageBankAccount = z.infer<typeof sageBankAccountSchema>

/**
 * Response shape returned after a Sage Intacct bank account sync run.
 * Reconcile pattern: snapshot existing → upsert → flag missing.
 */
export interface SageBankAccountSyncResult {
  /** Total number of Sage bank accounts successfully processed */
  synced_count: number
  /** Number of new rows inserted into sage_bank_accounts */
  created_count: number
  /** Number of existing rows updated in sage_bank_accounts */
  updated_count: number
  /** Number of rows flagged as missing in Sage after the sync */
  marked_missing_count: number
  /** List of per-row error messages collected during the sync run */
  errors: string[]
  /** Structured warnings persisted to sage_sync_state.warnings. */
  warnings: SageSyncWarning[]
}

/**
 * Represents a single GLACCOUNTBALANCE row returned by the Sage Intacct XML
 * Gateway. Sage returns one row per dimension combination (location, department,
 * etc.) for a given account + period + book, so the actual Intacct balance for
 * a bank account is computed as Σ ENDBAL across all rows matching
 * ACCOUNTNO=<bank gl_account_no>, PERIOD='Month Ended <Mo> <Yr>',
 * BOOKID='ACCRUAL'. Field names are kept uppercase to match the Sage object
 * schema verbatim.
 *
 * ENDBAL uses the same REJECT_NAN/coerce+refine+default(0) pattern as the
 * numeric fields in sageCheckSchema so non-numeric Sage payloads are rejected
 * with a clear error rather than silently producing NaN.
 */
export const sageGlAccountBalanceSchema = z.object({
  /** GL account number — matches sage_bank_accounts.gl_account_no. */
  ACCOUNTNO: z.string(),
  /** Ending balance for this dimension row. Sum across all rows for the
   *  account+period+book to get the full Intacct bank balance. */
  ENDBAL: z.coerce
    .number()
    .refine((val) => Number.isFinite(val), REJECT_NAN)
    .default(0),
  /** Accounting book identifier; the balance sync targets BOOKID='ACCRUAL'. */
  BOOKID: z.string().optional(),
  /** Period label as returned by Sage (e.g. "Month Ended June 2026"). */
  PERIOD: z.string().optional(),
})

export type SageGlAccountBalance = z.infer<typeof sageGlAccountBalanceSchema>

/**
 * Response shape returned after a Sage Intacct balance sync run (DCP-159).
 * Balances are upserted onto existing sage_bank_accounts rows — no rows are
 * created or marked missing, so marked_missing_count is intentionally absent.
 */
export interface SageBalanceSyncResult {
  /** Total number of bank accounts whose balance was successfully processed */
  synced_count: number
  /** Number of new rows inserted (expected 0 — balances go on existing accounts) */
  created_count: number
  /** Number of existing sage_bank_accounts rows updated with new balance data */
  updated_count: number
  /** List of per-account error messages collected during the sync run */
  errors: string[]
  /** Structured warnings persisted to sage_sync_state.warnings. */
  warnings: SageSyncWarning[]
}

/**
 * Represents a single Sage Intacct LOCATION record returned by the XML Gateway
 * (DCP-262 — auto-import of facilities). A LOCATION is a Sage organizational
 * entity (site / office / legal entity) that the facilities auto-import pulls
 * into the tenant `facilities` catalog as INACTIVE rows for the admin to
 * curate. Field names are kept uppercase to match Sage's object schema
 * verbatim; the sync task maps them to snake_case `facilities` columns.
 *
 * Only the two fields the sync actually projects and consumes are modelled —
 * LOCATIONID (the natural key → `facilities.sage_location_id`) and NAME (the
 * imported facility name). The task deliberately does NOT request STATUS /
 * MEGAENTITYID / PARENTID: they are unused, and asking Sage for a field the
 * LOCATION object doesn't expose fails the whole ReadByQuery with a generic
 * DL02 error. Add a field here only when the task starts projecting it.
 *
 * LOCATIONID is required; NAME is optional so a sparse Sage payload (Sage omits
 * empty fields) never fails the whole pull. Unknown extra fields are dropped by
 * Zod's default object behaviour, which is the leniency we want here.
 */
export const sageLocationSchema = z.object({
  /** Sage LOCATION natural key — the Sage Facility ID (maps to facilities.sage_location_id). */
  LOCATIONID: z.string().min(1, 'LOCATIONID is required'),
  /** Long legal name of the location; used as the imported facility name (falls back to LOCATIONID). */
  NAME: z.string().optional(),
})

export type SageLocation = z.infer<typeof sageLocationSchema>

/**
 * Response shape returned after a Sage Intacct facilities auto-import run
 * (DCP-262).
 *
 * The import is INSERT-ONLY: existing facilities (matched by Sage LOCATIONID)
 * are never touched so the admin's curation (short name, active flag, pack
 * assignment) is preserved. Hence the counts distinguish "pulled from Sage"
 * from "newly created" from "skipped because already present".
 */
export interface SageFacilitySyncResult {
  /** Total number of Sage LOCATION rows pulled and validated in this run. */
  pulled_count: number
  /** Number of NEW facilities inserted (Sage locations not already present). */
  created_count: number
  /** Number of Sage locations skipped because a facility already maps to them. */
  skipped_existing_count: number
  /** List of per-row error messages collected during the import run. */
  errors: string[]
  /** Structured warnings persisted to sage_sync_state.warnings. */
  warnings: SageSyncWarning[]
}
